import {FC, ReactNode, useEffect} from 'react'
import {Button, ButtonProps} from '../button/Button'
import {Divider} from '@components/ui/divider/Divider.tsx'
import {Spinner} from '@components/ui/spinner/Spinner.tsx'
import {useTranslation} from '@/translations/i18n.tsx'
import {useFormContext, UseFormReturn} from 'react-hook-form'
import {NotificationUsersConfirmationValidationSchema} from '@/features/users/types.ts'
import {DialogStore} from '@ariakit/react'
import {
    Dialog,
    DialogBody,
    DialogFooter,
    DialogHeader,
    DialogProvider
} from '@components/ui/dialog-atoms/DialogAtoms.tsx'
import {Tooltip} from '@/components/ui/tooltip/Tooltip'
import {useShallow} from 'zustand/react/shallow'
import {useConfirmationDialogStore} from './confirmationDialogStore'
import {ConfirmationDialogActions} from './ConfirmationDialogActions'
import {ConfirmationDialogAccordion} from './ConfirmationDialogAccordion'
import {ConfirmationDialogAccordionNotification} from './ConfirmationDialogAccordionNotification'
import {ConfirmationDialogConversation} from './ConfirmationDialogConversation'
import {NewConversationFormValidationSchema} from '@/features/conversations/types'
import {Text} from '../text/Text'
import {Flexbox} from '../flexbox/FlexBox'

interface ConfirmationDialogProps {
    onConfirm: () => void
    isConfirmPending: boolean
    headerSlot: ReactNode
    contentSlot?: ReactNode
    footerLeadingSlot?: ReactNode
    dialogStore: DialogStore
    hideDialogBody?: boolean
    confirmButtonVariant?: ButtonProps['variant']
    confirmButtonText?: string
    confirmButtonDisabled?: boolean
    confirmButtonTooltipContent?: string
    conversationForm?: UseFormReturn<NewConversationFormValidationSchema>
    showNotification?: boolean
}

export const ConfirmationDialog: FC<ConfirmationDialogProps> = ({
    headerSlot,
    contentSlot,
    footerLeadingSlot,
    onConfirm,
    isConfirmPending,
    hideDialogBody,
    dialogStore,
    confirmButtonVariant = 'primaryNeutral',
    confirmButtonText,
    confirmButtonDisabled,
    confirmButtonTooltipContent,
    conversationForm,
    showNotification
}) => {
    const {t} = useTranslation()
    const {accordionType, isAccordionOpen, resetStore, setSelectedMembers} = useConfirmationDialogStore(
        useShallow(state => ({
            accordionType: state.accordionType,
            isAccordionOpen: state.isAccordionOpen,
            setSelectedMembers: state.setSelectedMembers,
            resetStore: state.resetStore
        }))
    )

    const form = useFormContext<NotificationUsersConfirmationValidationSchema>()

    const renderNotificationAccordion = () => {
        return (
            <>
                <ConfirmationDialogAccordion />
                {isAccordionOpen && <ConfirmationDialogAccordionNotification />}
            </>
        )
    }

    const onChangeTypeSideEffects = () => {
        form.setValue('notificationsRecipients', [])
        setSelectedMembers([])
        conversationForm?.reset()
    }

    useEffect(() => {
        return () => resetStore()
    }, [])

    return (
        <DialogProvider store={dialogStore}>
            <Dialog width="460px" overflowY="unset">
                <DialogHeader>{headerSlot}</DialogHeader>
                <Divider direction="horizontal" />
                {!!contentSlot && (
                    <>
                        <DialogBody>{contentSlot}</DialogBody>
                        <Divider direction="horizontal" />
                    </>
                )}
                {!hideDialogBody && (
                    <>
                        <DialogBody>
                            {(conversationForm || showNotification) && (
                                <Flexbox direction="column" gap={2} fullWidth>
                                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                                        {t('confirmation_dialog:after_confirmation')}
                                    </Text>
                                    <ConfirmationDialogActions
                                        onChangeType={onChangeTypeSideEffects}
                                        showConversation={!!conversationForm}
                                        showNotification={!!showNotification}
                                    />
                                </Flexbox>
                            )}

                            {accordionType == 'no-action' &&
                                (!conversationForm && !showNotification ? (
                                    <Text palette="neutral" shade="950" size="textSm" style={{fontWeight: '500'}}>
                                        {t('confirmation_dialog:generic_question')}
                                    </Text>
                                ) : (
                                    <Text palette="neutral" shade="950" size="textSm">
                                        {t('confirmation_dialog:no_action_description')}
                                    </Text>
                                ))}
                            {accordionType == 'notification' && renderNotificationAccordion()}
                            {accordionType == 'conversation' && conversationForm && (
                                <ConfirmationDialogConversation conversationForm={conversationForm} />
                            )}
                        </DialogBody>
                        <Divider direction="horizontal" />
                    </>
                )}
                <DialogFooter>
                    <Button variant="secondary" onClick={() => dialogStore.setOpen(false)} disabled={isConfirmPending}>
                        {t('commons:cancel')}
                    </Button>
                    <Flexbox gap={2}>
                        {footerLeadingSlot}
                        {confirmButtonTooltipContent ? (
                            <Tooltip content={confirmButtonTooltipContent} variant="light">
                                <Button
                                    variant={confirmButtonVariant}
                                    onClick={onConfirm}
                                    disabled={isConfirmPending || confirmButtonDisabled}
                                >
                                    {confirmButtonText || t('commons:confirm')}
                                    {isConfirmPending && <Spinner size={14} />}
                                </Button>
                            </Tooltip>
                        ) : (
                            <Button
                                variant={confirmButtonVariant}
                                onClick={onConfirm}
                                disabled={isConfirmPending || confirmButtonDisabled}
                            >
                                {confirmButtonText || t('commons:confirm')}
                                {isConfirmPending && <Spinner size={14} />}
                            </Button>
                        )}
                    </Flexbox>
                </DialogFooter>
            </Dialog>
        </DialogProvider>
    )
}
